import React, { useState, useEffect, useContext } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { CartProvider } from '../context/CartContext';
import { OrderProvider } from '../context/OrderContext';
import { UserProvider } from '../context/UserContext';
import { AuthProvider, AuthContext } from '../context/AuthContext';
import Configurator from './Configurator';
import OrdersList from './OrdersList';
import Cart from './Cart';
import CartClassic from './CartClassic';
import Orders from './Orders';
import Alimentatori from './Alimentatori';
import LooseProfilesConfigurator from './LooseProfilesConfigurator';
import StripLed from './StripLed';
import Accessori from './Accessori';
import BTMConfigurator from './BTMConfigurator';
import Sidebar from './Sidebar';
import Header from './Header';
import Fuse from 'fuse.js';
import Login from './Login';
import AdminDashboard from './AdminDashboard';
import authService from '../services/authService';
import '../css/App.css';
import CreateUser from './CreateUser';
import Dashboard from './Dashboard';
import Catalogo from './Catalogo'; // Importa il componente Catalogo
import OnSale from './OnSale';
import NewArrivals from './NewArrivals';
import ProductManager from './ProductManager';
import PrivacyPolicy from './PrivacyPolicy';
import { LanguageProvider } from '../context/LanguageContext';

const SIDEBAR_EXPANDED_KEY = 'b2b_sidebar_expanded';

function App() {
  const getShowPricesKey = (u) => (u?.email ? `showPrices_${u.email}` : u?.id ? `showPrices_${u.id}` : 'showPrices_default');

  const [alimentatori, setAlimentatori] = useState([]);
  const [stripLeds, setStripLeds] = useState([]);
  const [accessori, setAccessori] = useState([]);
  const [looseProfiles, setLooseProfiles] = useState([]);
  const [catalogoProducts, setCatalogoProducts] = useState([]); // Nuovo stato per i prodotti del catalogo
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const { user, loading, setUser } = useContext(AuthContext);
  // Barra laterale: 'desktop' (icone o estesa), 'laptop' (icone, ☰ apre sopra), 'phone' (nascosta, ☰ apre pannello)
  const [layoutMode, setLayoutMode] = useState('desktop');
  const [sidebarExpanded, setSidebarExpanded] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_EXPANDED_KEY) === 'true';
    } catch (err) {
      return false;
    }
  });
  const [overlayOpen, setOverlayOpen] = useState(false);
  const isMobile = layoutMode === 'phone';

  // Nuovo stato per la visualizzazione dei prezzi
  const [showPrices, setShowPrices] = useState(true);

  // Funzione per alternare la visualizzazione dei prezzi
  const togglePrices = () => {
    setShowPrices((prev) => {
      const next = !prev;
      const key = getShowPricesKey(user);
      try {
        localStorage.setItem(key, JSON.stringify(next));
      } catch (err) {
        console.warn('Impossibile salvare showPrices su localStorage', err);
      }
      return next;
    });
  };

  // Ricarica la preferenza showPrices dall'utente corrente
  useEffect(() => {
    if (!user) {
      setShowPrices(true); // default quando non loggato
      return;
    }

    const key = getShowPricesKey(user);
    const stored = localStorage.getItem(key);
    if (stored !== null) {
      setShowPrices(stored === 'true');
    } else {
      setShowPrices(true);
    }
  }, [user]);

  // Verifica della scadenza del token JWT
  // useEffect(() => {
  //   const checkTokenExpiry = async () => {
  //     const token = localStorage.getItem('token'); // Ottieni il token dal localStorage
  //     if (token != null) {
  //       console.log("token:", token, "token-parts:", token.split("."));
  //       const payload = JSON.parse(atob(token.split('.')[1])); // Decodifica il payload del token
  //       const isExpired = payload.exp * 1000 < Date.now(); // Verifica se il token è scaduto
  //       if (isExpired) {
  //         localStorage.removeItem('token'); // Rimuovi il token
  //         localStorage.removeItem('user'); // Rimuovi anche l'utente se necessario
  //         setUser(null); // Imposta l'utente su null
  //         window.location.href = '/login'; // Reindirizza al login
  //       } else {
  //         // Se il token è valido, imposta l'utente dal localStorage
  //         let user = JSON.parse(localStorage.getItem('user'));
  //         if (user) {
  //           setUser(user); // Imposta l'utente nel contesto di autenticazione

  //           try {
  //             // Recupera i dati aggiornati dell'utente dal server
  //             const updatedUser = await authService.getCurrentUser(token);
  //             setUser(updatedUser); // Aggiorna il contesto con i dati aggiornati

  //             // Salva l'utente aggiornato nel localStorage
  //             localStorage.setItem('user', JSON.stringify(updatedUser));
  //             setReady(true);
  //           } catch (error) {
  //             console.error("Errore nel recuperare i dati aggiornati dell'utente:", error);
  //             // In caso di errore (ad esempio token scaduto), fai il logout
  //             localStorage.removeItem('token');
  //             localStorage.removeItem('user');
  //             debugger;
  //             setUser(null);
  //             window.location.href = '/login'; // Reindirizza al login
  //           }
  //         }
  //       }
  //     }
  //   };

  //   if (user) {
  //     checkTokenExpiry(); // Esegui il controllo del token scaduto
  //   }

  // }, [user, setUser]);

  useEffect(() => {
    const fetchData = async () => {
      const alimentatoriResponse = await fetch('/api/products/config/alimentatori');
      const alimentatoriData = await alimentatoriResponse.json();
      setAlimentatori(alimentatoriData);

      const stripLedsResponse = await fetch('/api/products/config/stripled');
      const stripLedsData = await stripLedsResponse.json();
      setStripLeds(stripLedsData);

      const looseProfilesResponse = await fetch('/api/products/config/configlooseprofiles');
      const looseProfilesData = await looseProfilesResponse.json();
      setLooseProfiles(looseProfilesData);

      // Fetch per i prodotti del catalogo
      const catalogoResponse = await fetch('/api/products/public');
      const catalogoData = await catalogoResponse.json();
      setCatalogoProducts(catalogoData); // Salva i prodotti del catalogo

      // Accessori: per ultimi e senza bloccare il resto se il file non c'è ancora
      try {
        const accessoriResponse = await fetch('/api/products/config/accessori');
        const accessoriData = await accessoriResponse.json();
        setAccessori(Array.isArray(accessoriData) ? accessoriData : []);
      } catch (e) {
        setAccessori([]);
      }
    };

    if (user) {
      console.log("authContext user:", user);
      fetchData();
    }
  }, [user]);

  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      const next = w <= 768 ? 'phone' : w <= 1100 ? 'laptop' : 'desktop';
      setLayoutMode((prev) => {
        if (prev !== next) setOverlayOpen(false);
        return next;
      });
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Larghezza barra e altezza intestazione valgono per tutte le pagine
  const sidebarWidth = layoutMode === 'phone' ? 0 : layoutMode === 'desktop' && sidebarExpanded ? 212 : 64;
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--sidebar-width', `${sidebarWidth}px`);
    root.style.setProperty('--header-height', '60px');
  }, [sidebarWidth]);

  // Esc chiude il pannello aperto
  useEffect(() => {
    if (!overlayOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOverlayOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [overlayOpen]);

  const toggleExpanded = () => {
    setSidebarExpanded((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_EXPANDED_KEY, String(next));
      } catch (err) {
        // preferenza non salvata: pazienza
      }
      return next;
    });
  };
  const handleToggleSidebar = () => {
    if (layoutMode === 'desktop') toggleExpanded();
    else setOverlayOpen((prev) => !prev);
  };
  const handleCloseSidebarOnNavigate = () => setOverlayOpen(false);
  const isSidebarOpen = layoutMode === 'desktop' ? sidebarExpanded : overlayOpen;

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (!query) {
      setSearchResults([]);
      return;
    }

    console.log("Catalogo Products disponibili per la ricerca:", catalogoProducts);

    const fuseOptions = {
      shouldSort: true,
      includeScore: true,
      threshold: 0.4, // Aumenta leggermente la soglia
      location: 0,
      distance: 100,
      minMatchCharLength: 1, // Assicura che anche corrispondenze corte siano considerate
      keys: ['name', 'id', 'descizione'], // Campi su cui eseguire la ricerca
    };

    console.log("Esecuzione della ricerca per query:", query);

    const fuseAlimentatori = new Fuse(alimentatori, fuseOptions);
    const fuseStripLeds = new Fuse(stripLeds, fuseOptions);
    const fuseAccessori = new Fuse(accessori, fuseOptions);
    const fuseLooseProfiles = new Fuse(looseProfiles, fuseOptions);
    const fuseCatalogo = new Fuse(catalogoProducts, fuseOptions);

    // Risultati non processati
    const results = [
      ...fuseAlimentatori.search(query).map(result => ({ ...result.item, source: 'alimentatori' })),
      ...fuseStripLeds.search(query).map(result => ({ ...result.item, source: 'stripLeds' })),
      ...fuseAccessori.search(query).map(result => ({ ...result.item, source: 'accessori' })),
      ...fuseLooseProfiles.search(query).map(result => ({ ...result.item, source: 'looseProfiles' })),
      ...fuseCatalogo.search(query).map(result => ({ ...result.item, source: 'catalogo' })),
    ];

    console.log("Risultati della ricerca (non processati):", results);

    // Processa i risultati della ricerca per essere usati nei componenti
    const processedResults = results.map(result => result); // Non serve accedere a `result.item` perché hai già estratto `item`
    console.log("Risultati della ricerca (processati):", processedResults);

    setSearchResults(processedResults); // Imposta i risultati di ricerca
  };

  // Filtra i risultati di ricerca per componente
  const catalogoSearchResults = searchResults.filter(product => product.source === 'catalogo');
  const alimentatoriSearchResults = searchResults.filter(product => product.source === 'alimentatori');
  const stripLedsSearchResults = searchResults.filter(product => product.source === 'stripLeds');
  const accessoriSearchResults = searchResults.filter(product => product.source === 'accessori');
  const looseProfilesSearchResults = searchResults.filter(product => product.source === 'looseProfiles');

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await authService.logout(token);
      }
      setUser(null);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    } catch (error) {
      console.error('Errore durante il logout:', error);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <CartProvider>
      <OrderProvider>
        <UserProvider>
          <Router>
            <div className="App">
              {user ? (
                <>
                  <Sidebar
                    mode={layoutMode}
                    expanded={sidebarExpanded}
                    overlayOpen={overlayOpen}
                    onNavigate={handleCloseSidebarOnNavigate}
                    onToggleExpanded={toggleExpanded}
                  />
                  {overlayOpen && <div className="sb-backdrop" onClick={() => setOverlayOpen(false)} />}
                  <div className={`main-content ${isMobile ? 'mobile' : ''}`}>
                    {/* Passa la funzione togglePrices all'Header */}
                    <Header
                      user={user}
                      onLogout={handleLogout}
                      onSearch={handleSearch}
                      togglePrices={togglePrices}
                      showPrices={showPrices}
                      onToggleSidebar={handleToggleSidebar}
                      isSidebarOpen={isSidebarOpen}
                      isMobile={isMobile}
                    />
                    <Routes>
                      {/* Reindirizzamento condizionale alla dashboard */}
                      <Route path="/" element={<Navigate to="/dashboard" />} />
                      <Route path="/dashboard" element={<Dashboard showPrices={showPrices} />} />
                      <Route path="/configurator" element={<Configurator showPrices={showPrices} />} />
                      <Route path="/cart" element={<Cart />} />
                      <Route path="/cart/classic" element={<CartClassic />} />
                      <Route path="/orders" element={<Orders searchQuery={searchQuery} />} />
                      <Route path="/orderslist" element={<OrdersList />} />
                      <Route path="/alimentatori" element={<Alimentatori searchResults={alimentatoriSearchResults} showPrices={showPrices} />} />
                      <Route path="/looseprofiles" element={<LooseProfilesConfigurator searchResults={looseProfilesSearchResults} showPrices={showPrices} />} />
                      <Route path="/btmconfigurator" element={<BTMConfigurator onSearch={handleSearch} searchResults={searchResults} showPrices={showPrices} />} />

                      {/* Passa lo stato showPrices e i risultati di ricerca filtrati a Catalogo */}
                      <Route path="/catalogo" element={<Catalogo showPrices={showPrices} searchResults={catalogoSearchResults} />} />
                      <Route path="/stripled" element={<StripLed searchResults={stripLedsSearchResults} showPrices={showPrices} />} />
                      <Route path="/accessori" element={<Accessori searchResults={accessoriSearchResults} showPrices={showPrices} />} />
                      <Route path="/create-user" element={<CreateUser token={user.token} />} />
                      <Route path="/onsale" element={<OnSale showPrices={showPrices} />} />
                      <Route path="/novita" element={<NewArrivals showPrices={showPrices} />} />
                      <Route path="/privacy" element={<PrivacyPolicy />} />

                      {user.role === 'admin' && (
                        <>
                          <Route path="/admin-dashboard" element={<AdminDashboard />} />
                          <Route path="/product-manager" element={<ProductManager />} />
                        </>
                      )}

                      {/* Catch-all: reindirizza a /dashboard se la rotta non è trovata */}
                      <Route path="*" element={<Navigate to="/dashboard" />} />
                    </Routes>
                  </div>
                </>
              ) : (
                <Routes>
                  {/* Reindirizzamento condizionale alla pagina di login */}
                  <Route path="/" element={<Navigate to="/login" />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Navigate to="/login?view=register" replace />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />

                  {/* Catch-all: reindirizza a /login se la rotta non è trovata */}
                  <Route path="*" element={<Navigate to="/login" />} />
                </Routes>
              )}
            </div>
          </Router>
        </UserProvider>
      </OrderProvider>
    </CartProvider>
  );
}

const AppWrapper = () => (
  <LanguageProvider>
    <AuthProvider>
      <App />
    </AuthProvider>
  </LanguageProvider>
);

export default AppWrapper;
